import { useEffect, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { useSession } from "../../auth/session"
import { useDispatch, useSelector } from "react-redux"
import { setUserAccessThunk, useUser } from "../../redux/usersSlice"
import { meSelector } from "../../redux/globalSlice"
import { AppDispatch } from "../../redux/store"
import { FetchableStateStatus } from "../../redux/fetchableState"
import { User, UserRole } from "../../api/models"
import Spinner from "../widgets/Spinner"
import Avatar from "../widgets/Avatar"
import EntitlementsTable from "../EntitlementsTable"
import UserLicensesTable from "../UserLicensesTable"
import UserIlokAccounts from "../UserIlokAccounts"
import { sameUserId } from "../../utils/userIds"
import { backdropDismiss } from "../../utils/backdropDismiss"

const roleLabels: Record<string, string> = {
  admin: "Admin",
  customer: "Customer",
}

function formatDate(d?: Date | string | null) {
  if (!d) return null
  const date = typeof d === "string" ? new Date(d) : d
  return date.toLocaleDateString()
}

// Confirmation for the destructive direction only (enabling is harmless and
// happens without ceremony). Disabling signs the user out everywhere; their
// already-issued access tokens can stay valid for up to an hour.
function DisableAccessDialog({ user, accessToken, onClose }: {
  user: User
  accessToken: string
  onClose: () => void
}) {
  const dispatch = useDispatch<AppDispatch>()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [step, setStep] = useState<"confirm" | "disabling" | "error">("confirm")
  const [errorMessage, setErrorMessage] = useState("")

  useEffect(() => {
    dialogRef.current?.showModal()
  }, [])

  const handleDisable = async () => {
    setStep("disabling")
    setErrorMessage("")
    try {
      await dispatch(setUserAccessThunk({ id: user.id, updates: { enabled: false }, accessToken })).unwrap()
      dialogRef.current?.close()
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "unknown error")
      setStep("error")
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="redeem-dialog"
      onClose={onClose}
      {...backdropDismiss(() => { if (step !== "disabling") dialogRef.current?.close() })}
    >
      <div className="redeem-dialog-content">
        <button className="redeem-dialog-close" onClick={() => dialogRef.current?.close()} aria-label="Close">&times;</button>
        <h2 className="redeem-dialog-title">Disable Access</h2>
        <p>
          This disables <strong>{user.id}</strong>'s account: they are signed out everywhere and
          cannot sign in again until re-enabled. Sessions already issued may keep working for up
          to an hour.
        </p>

        {step === "error" && errorMessage && (
          <p className="generate-codes-error">{errorMessage}</p>
        )}

        <div className="redeem-actions">
          <button onClick={() => dialogRef.current?.close()} disabled={step === "disabling"}>Cancel</button>
          <button
            className="btn-upgrade"
            disabled={step === "disabling"}
            onClick={handleDisable}
          >
            {step === "disabling" ? <><Spinner size={12} /> Disabling…</> : "Disable access"}
          </button>
        </div>
      </div>
    </dialog>
  )
}

export default function UserPage() {
  const { id } = useParams<{ id: string }>()
  const session = useSession()
  const accessToken = session.accessToken ?? ""
  const dispatch = useDispatch<AppDispatch>()
  const me = useSelector(meSelector)?.data
  const { data: user, status } = useUser(id!, accessToken)
  const [showDisableDialog, setShowDisableDialog] = useState(false)
  // bumped when an iLok unlink revokes entitlements, so the tables below
  // (which own their own fetches) remount and refetch
  const [tablesKey, setTablesKey] = useState(0)

  if (status === FetchableStateStatus.Error) {
    return (
      <div className="page">
        <p className="empty-state">Error loading user</p>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="page">
        <p className="empty-state"><Spinner size={16} /> Loading user…</p>
      </div>
    )
  }

  const fullName = [user.firstname, user.lastname].filter(Boolean).join(" ")

  return (
    <div className="page">
      {/* header: avatar + username + role pill (+ disabled pill when applicable) */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
        <Avatar imageUrl={user.imageUrl} size={80} />
        <h1 className="page-title" style={{ margin: 0, wordBreak: "break-all" }}>{user.id}</h1>
        <span className="status-badge status-active" style={{ flexShrink: 0 }}>
          {roleLabels[user.role] ?? user.role}
        </span>
        {user.enabled === false && (
          <span className="status-badge status-expired" style={{ flexShrink: 0 }}>
            🚫 Disabled
          </span>
        )}
        {/* admin-only, never on your own account (backend enforces both) */}
        {me?.role === UserRole.Admin && !sameUserId(me.id, user.id) && (
          user.enabled === false ? (
            <button
              className="card-link"
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0, marginLeft: "auto" }}
              onClick={() => dispatch(setUserAccessThunk({ id: user.id, updates: { enabled: true }, accessToken }))}
            >
              Enable access
            </button>
          ) : (
            <button
              className="card-link-danger"
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0, marginLeft: "auto" }}
              onClick={() => setShowDisableDialog(true)}
            >
              Disable access
            </button>
          )
        )}
      </div>

      {user.email && (
        <p style={{ margin: "0 0 4px" }}>
          <a href={`mailto:${user.email}`} className="card-link">{user.email}</a>
          {user.emailVerified === false && (
            <span title="Email not verified" style={{ marginLeft: 6, cursor: "help" }} role="img" aria-label="Email not verified">
              ⚠️
            </span>
          )}
        </p>
      )}

      {fullName && (
        <p style={{ margin: "0 0 4px", color: "var(--nfa-light-grey)" }}>{fullName}</p>
      )}

      <div className="detail-grid" style={{ marginTop: 24 }}>
        <div className="detail-item">
          <div className="detail-label">Two-Factor Auth</div>
          <div className="detail-value">{user.mfaEnabled ? "Enabled" : "Disabled"}</div>
        </div>
        <div className="detail-item">
          <div className="detail-label">Member since</div>
          <div className="detail-value">{formatDate(user.createdAt) ?? "—"}</div>
        </div>
      </div>

      {/* developers see only the slice on skus they own (server-side scoping) */}
      <h2 className="section-title" style={{ marginTop: 32 }}>SKUs</h2>
      <EntitlementsTable key={`entitlements-${tablesKey}`} userId={user.id} showSkuColumn accessToken={accessToken} />

      {/* developers see only the slice on products their skus claim (server-side scoping) */}
      <h2 className="section-title" style={{ marginTop: 32 }}>Licenses</h2>
      <UserLicensesTable key={`licenses-${tablesKey}`} userId={user.id} accessToken={accessToken} />

      {/* admin-only: the user's iLok.com accounts, each unlinkable (the
          backend rejects developers, so the section stays hidden for them) */}
      {me?.role === UserRole.Admin && (
        <>
          <h2 className="section-title" style={{ marginTop: 32 }}>iLok Accounts</h2>
          <UserIlokAccounts userId={user.id} accessToken={accessToken} onUnlinked={() => setTablesKey((k) => k + 1)} />
        </>
      )}

      {showDisableDialog && (
        <DisableAccessDialog
          user={user}
          accessToken={accessToken}
          onClose={() => setShowDisableDialog(false)}
        />
      )}
    </div>
  )
}
